import { derived, readable, type Readable } from "svelte/store";
import { userStore, type User } from "./user";
import {
  chainStores,
  getDocumentAsStore,
  getSubCollectionAsStore,
  mergeLatestOfEachToArray,
} from "$lib/utils/firestore";
import { params } from "svelte-spa-router";
import { toaster } from "$lib/utils/toaster";

interface RawOrgData {
  orgId: string;
  data: any;
  claims: string[];
}

export interface OrgMember {
  email: string;
  claims: string[];
  modified: Date;
}

const rawOrgsStore = chainStores<User, RawOrgData[]>(userStore, [], (user) => {
  if (!user) {
    return readable([]);
  }
  const orgStores = user.orgClaims.map((orgRef) => {
    return getDocumentAsStore({
      docPath: `organisations/${orgRef.orgId}`,
      extraData: { claims: orgRef.claims },
      onError: (err) => {
        console.log(err.message);
        toaster.error("Error", `Error reading '${orgRef.orgId}'. Please contact support. Detail: ${err.message}`);
        return undefined;
      },
    });
  });
  return derived(mergeLatestOfEachToArray(orgStores), (orgs) => {
    return orgs
      .filter((o) => o != undefined)
      .map((org) => ({
        orgId: org.id,
        data: org.data,
        claims: org.claims,
      }));
  });
});

rawOrgsStore.subscribe((u) => console.log("RawOrgs store changed", u));

export interface OrgRef {
  id: string;
  alias: string;
}

export const orgsListStore = derived<Readable<RawOrgData[]>, OrgRef[]>(rawOrgsStore, (rawOrgs) => {
  return rawOrgs.map<OrgRef>((rawOrg) => {
    return {
      id: rawOrg.orgId,
      alias: (rawOrg.data.alias ?? rawOrg.orgId) as string,
    };
  });
});

orgsListStore.subscribe((u) => console.log("OrgRefList store changed", u));

export interface OrgMember {
  email: string;
  claims: string[];
  modified: Date;
}

interface ConnectionSource {
  provider: string;
  invitedAdmins: string[];
  connected?: boolean;
  connected_by?: string;
  tenant_id?: string;
  scopes?: string[];
}

export interface Organisation {
  id?: string;
  onboarding: boolean;
  pagePrefix: string;
  resourcePrefix: string;
  alias?: string;
  index?: {
    managed: boolean;
  };
  members?: OrgMember[];
  domains?: string[];
  sdp_finding_root_directories?: string[];
  dataSources: ConnectionSource[];
  sso: ConnectionSource;
}

export const orgIdFromPath = readable<string | undefined>(undefined, (set) => {
  let lastOrgId: string | null = null;
  const unsub = params.subscribe((currentParams) => {
    if (currentParams?.orgId && currentParams.orgId !== lastOrgId) {
      set(currentParams.orgId);
      lastOrgId = currentParams.orgId;
    }
  });
  return () => {
    unsub();
  };
});

export const selectedOrgStore: Readable<Organisation | undefined> = chainStores(
  derived([userStore, rawOrgsStore, orgIdFromPath], (all) => all),
  undefined,
  ([userStore, rawOrgs, orgId]) => {
    if (userStore?.organisation_setup_id) {
      return derived(
        getDocumentAsStore({
          docPath: `organisation_setups/${userStore.organisation_setup_id}`,
          onError: (err) => {
            // TODO: check err is unauthed?
            console.log("Lost access to organisation setup, expected during org promotion");
            return undefined;
          },
        }),
        (rawOrgSetup) => {
          const org: Organisation = {
            id: undefined,
            onboarding: true,
            pagePrefix: `/onboarding`,
            resourcePrefix: `/org-setup/${userStore.organisation_setup_id}`,
            sso: {
              provider: rawOrgSetup?.data.sso?.provider,
              invitedAdmins: rawOrgSetup?.data.sso?.invited_admins || [],
              connected: false,
            },
            dataSources:
              rawOrgSetup?.data.data_sources?.map((ds: any) => ({
                provider: ds?.provider,
                invitedAdmins: ds?.invited_admins || [],
                connected: !!ds?.connected_by,
                connected_by: ds?.connected_by,
                tenant_id: ds?.tenant_id,
                scopes: ds?.scopes,
              })) ?? [],
            sdp_finding_root_directories: rawOrgSetup?.data.sdp_finding_root_directories,
          };
          return org;
        },
      );
    }
    if (!orgId && rawOrgs.length === 0) {
      return readable(undefined);
    }
    const org = orgId ? rawOrgs.find((ro) => ro.orgId === orgId) : rawOrgs[0];
    if (!org) {
      return readable(undefined);
    }

    return derived(
      [
        getSubCollectionAsStore(`organisations/${org.orgId}/members`),
        getSubCollectionAsStore(`organisations/${org.orgId}/domains`),
        getSubCollectionAsStore(`organisations/${org.orgId}/ingestion_filters`),
      ],
      ([memberItems, domainItems, ingestionFilters]) => {
        const dataSources: ConnectionSource[] =
          org.data.data_sources?.map((ds: any) => ({
            provider: ds?.provider,
            invitedAdmins: ds?.invited_admins || [],
            connected: !!ds?.connected_by,
            connected_by: ds?.connected_by,
            tenant_id: ds?.tenant_id,
            scopes: ingestionFilters.find((f) => f.id === ds.provider)?.data.scopes,
          })) ?? [];
        const selectedOrg: Organisation = {
          id: org.orgId,
          onboarding: !dataSources.some((ds) => ds.connected_by),
          pagePrefix: `/o/${org.orgId}`,
          resourcePrefix: `/orgs/${org.orgId}`,
          alias: org.data.alias ?? (domainItems.length > 0 ? domainItems[0].data.fqdn : org.orgId),
          index: {
            managed: org.data?.primary_index?.is_managed ?? true,
          },
          members: memberItems.map((i) => {
            return {
              email: i.id,
              claims: i.data.claims as string[],
              modified: i.data.modified?.toDate() as Date,
            };
          }),
          domains: domainItems.map((i) => {
            return i.data.fqdn;
          }),
          // TODO - get this from the schema
          dataSources: dataSources,
          sso: {
            provider: org.data.sso?.provider,
            invitedAdmins: [], // no longer show invites after SSO is connected
            connected: !!org.data.sso?.connected_by, // by definition = true
            connected_by: org.data.sso?.connected_by,
          },
          sdp_finding_root_directories: org?.data.sdp_finding_root_directories,
        };
        return selectedOrg;
      },
    );
  },
);

selectedOrgStore.subscribe((r) => console.log("Selected Org store changed", r));

export const selectedOrgIdStore = derived(selectedOrgStore, (org) => org?.id);
selectedOrgIdStore.subscribe((r) => console.log("Selected Org ID store changed", r));
